import React, {useCallback, useEffect, useState} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grow,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import {useSnackbar} from 'notistack';
import API_Service, {axiosURIencode} from '../../../../Services/API_Service';
import {req_leave_type, req_leave_type_status} from '../../../../Helper/Constants';
import {EmptyState, SectionToolbar, SettingSwitch, SettingsSurface, StatusSwitch, StatusTabs} from './ui';

const useStyles = makeStyles((theme) => ({
    addButton: {
        width: 40,
        height: 40,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 7px 16px rgba(0,0,0,.16)',
        transition: 'transform 140ms cubic-bezier(.34,1.3,.64,1), box-shadow 140ms ease',
        '&:hover': {backgroundColor: theme.palette.primary.main, transform: 'translateY(-2px) scale(1.04)', boxShadow: '0 10px 22px rgba(0,0,0,.2)'},
        '&:active': {transform: 'scale(.96)'}
    },
    listItem: {
        minHeight: 58,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(12),
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 120ms ease, transform 120ms ease',
        animation: '$rowIn 240ms cubic-bezier(0,0.4,0,1) both',
        '&:last-child': {borderBottom: 0},
        '&:hover': {backgroundColor: theme.palette.action.hover, transform: 'translateX(2px)'}
    },
    inactive: {opacity: 0.58},
    rowName: {fontWeight: 650},
    dialogPaper: {borderRadius: 16},
    dialogTitle: {paddingBottom: theme.spacing(1), fontWeight: 800},
    dialogContent: {
        paddingTop: `${theme.spacing(2)}px !important`,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2)
    },
    switchRow: {
        width: '100%',
        margin: 0,
        justifyContent: 'space-between'
    },
    stagger0: {animationDelay: '0ms'},
    stagger1: {animationDelay: '45ms'},
    stagger2: {animationDelay: '90ms'},
    stagger3: {animationDelay: '135ms'},
    stagger4: {animationDelay: '180ms'},
    stagger5: {animationDelay: '225ms'},
    stagger6: {animationDelay: '270ms'},
    stagger7: {animationDelay: '315ms'},
    '@keyframes rowIn': {from: {opacity: 0, transform: 'translateY(6px)'}, to: {opacity: 1, transform: 'translateY(0)'}}
}));

export default function LeaveTypeSection({axiosConfig, axiosPut, language}) {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const {enqueueSnackbar} = useSnackbar();
    const [items, setItems] = useState(null);
    const [statusFilter, setStatusFilter] = useState(1);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [ltCode, setLtCode] = useState('');
    const [ltName, setLtName] = useState('');
    const [reasonRequired, setReasonRequired] = useState(false);
    const [attachmentRequired, setAttachmentRequired] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setItems(null);
        API_Service.get(req_leave_type, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) setItems(data.extraData || []);
            else {
                setItems([]);
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setItems([]);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    }, [axiosConfig, enqueueSnackbar, language.operation_fail]);

    useEffect(() => { load(); }, [load]);

    const resetForm = () => {
        setEditId(null);
        setLtCode('');
        setLtName('');
        setReasonRequired(false);
        setAttachmentRequired(false);
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        resetForm();
    };

    const openAdd = () => {
        resetForm();
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditId(item.id);
        setLtCode(item.lt_code || '');
        setLtName(item.lt_name || '');
        setReasonRequired(parseInt(item.reason_required) === 1);
        setAttachmentRequired(parseInt(item.attachment_required) === 1);
        setDialogOpen(true);
    };

    const save = () => {
        const code = ltCode.trim();
        const name = ltName.trim();
        if (!code || !name) {
            enqueueSnackbar(language.required_fields, {variant: 'warning'});
            return;
        }
        if (saving) return;
        setSaving(true);
        const request = editId
            ? API_Service.put(req_leave_type, axiosURIencode({
                id: editId,
                lt_code: code,
                lt_name: name,
                reason_required: reasonRequired ? 1 : 0,
                attachment_required: attachmentRequired ? 1 : 0
            }), axiosPut)
            : (() => {
                const formdata = new FormData();
                formdata.append('lt_code', code);
                formdata.append('lt_name', name);
                formdata.append('reason_required', reasonRequired ? 1 : 0);
                formdata.append('attachment_required', attachmentRequired ? 1 : 0);
                return API_Service.post(req_leave_type, formdata, axiosConfig);
            })();
        request.then(({data}) => {
            setSaving(false);
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                setDialogOpen(false);
                resetForm();
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => {
            setSaving(false);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    };

    const [togglingId, setTogglingId] = useState(null);

    const toggleStatus = (item) => {
        setTogglingId(item.id);
        API_Service.put(req_leave_type_status, axiosURIencode({id: item.id}), axiosPut).then(({data}) => {
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'}))
            .finally(() => setTogglingId(null));
    };

    const filtered = (items || [])
        .filter((item) => parseInt(item.status) === parseInt(statusFilter))
        .filter((item) => {
            if (!search) return true;
            const s = search.toLowerCase();
            return String(item.lt_code || '').toLowerCase().includes(s) || String(item.lt_name || '').toLowerCase().includes(s);
        });

    return (
        <React.Fragment>
            <SectionToolbar search={search} onSearch={setSearch} placeholder={language.search}>
                <StatusTabs
                    value={statusFilter}
                    onChange={setStatusFilter}
                    activeLabel={language.active}
                    inactiveLabel={language.inactive}
                    ariaLabel={language.status || 'Status'}
                />
                <Tooltip title={language.add_leave_type}>
                    <IconButton className={classes.addButton} onClick={openAdd} aria-label={language.add_leave_type}><AddIcon/></IconButton>
                </Tooltip>
            </SectionToolbar>
            <SettingsSurface>
                {items === null ? (
                    <div>{[0, 1, 2, 3].map((row) => <Skeleton key={row} animation="wave" height={58}/>)}</div>
                ) : filtered.length === 0 ? <EmptyState label={language.no_data}/> : (
                    <List disablePadding>
                        {filtered.map((item, index) => {
                            const active = parseInt(item.status) === 1;
                            const tags = [];
                            if (parseInt(item.reason_required) === 1) tags.push(language.reason_required);
                            if (parseInt(item.attachment_required) === 1) tags.push(language.attachment_required);
                            return (
                                <ListItem
                                    key={item.id}
                                    className={`${classes.listItem} ${classes[`stagger${Math.min(index, 7)}`]} ${active ? '' : classes.inactive}`}
                                >
                                    <ListItemText
                                        primary={`${item.lt_code} - ${item.lt_name}`}
                                        primaryTypographyProps={{className: classes.rowName}}
                                        secondary={tags.length > 0 ? tags.join(' · ') : null}
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title={language.edit}>
                                            <IconButton size="small" onClick={() => openEdit(item)} aria-label={`${language.edit} ${item.lt_name}`}><EditIcon fontSize="small"/></IconButton>
                                        </Tooltip>
                                        <StatusSwitch active={active} pending={String(togglingId) === String(item.id)} name={item.lt_name} language={language} onToggle={() => toggleStatus(item)}/>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </SettingsSurface>
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Grow}
                PaperProps={{className: classes.dialogPaper}}
            >
                <DialogTitle className={classes.dialogTitle}>{editId ? language.edit_leave_type : language.add_leave_type}</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <TextField
                        autoFocus
                        fullWidth
                        variant="outlined"
                        label={language.leave_type_code}
                        value={ltCode}
                        onChange={(event) => setLtCode(event.target.value)}
                        inputProps={{maxLength: 20}}
                    />
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={language.leave_type_name}
                        value={ltName}
                        onChange={(event) => setLtName(event.target.value)}
                        inputProps={{maxLength: 255}}
                    />
                    <FormControlLabel
                        className={classes.switchRow}
                        labelPlacement="start"
                        control={<SettingSwitch checked={reasonRequired} onChange={(event) => setReasonRequired(event.target.checked)}/>}
                        label={language.reason_required}
                    />
                    <FormControlLabel
                        className={classes.switchRow}
                        labelPlacement="start"
                        control={<SettingSwitch checked={attachmentRequired} onChange={(event) => setAttachmentRequired(event.target.checked)}/>}
                        label={language.attachment_required}
                    />
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={closeDialog}>{language.cancel}</Button>
                    <Button variant="contained" color="primary" disabled={saving || !ltCode.trim() || !ltName.trim()} onClick={save}>
                        {saving ? <CircularProgress size={18}/> : editId ? language.update : language.add}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
